import { FormikErrors, FormikProps, FormikTouched } from 'formik'
import { useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { BusinessUnitInterface } from '../../interfaces'
import { getLockBUServices } from '../../services'
import { setLockedServices } from '../../store/slices'
import { filterBusinessUnit } from '../../utils/filters'
import { resolveCityFromBusinessUnit } from '../../utils/businessUnitCity'
import { BusinessUnitOption } from '../FormFields/FormSearchOptions'
import { FormSearch } from '../FormFields/FormSearch'
import { FormText } from '../FormFields/FormText'
import { FormTextArea } from '../FormFields/FormTextArea'

export interface OriginFormValues {
  businessUnit?: BusinessUnitInterface
  city: string
  cityCode: string
  address: string
}

interface OriginField {
  origin: OriginFormValues
}

interface OriginFormProps<T> {
  formik: FormikProps<T & OriginField>
}

export const OriginForm = <T extends any>({ formik }: OriginFormProps<T>) => {
  const dispatch = useAppDispatch()
  const cities = useAppSelector((state) => state.inmutable.cities)
  // Origen puede ser de otro país → usar el catálogo de todos los países,
  // con fallback al de destino (VE) si aún no se hidrató.
  const originCitiesAll = useAppSelector(
    (state) => state.inmutable.originCities
  )
  const originCities = originCitiesAll.length > 0 ? originCitiesAll : cities
  const businessUnits = useAppSelector((state) => state.inmutable.businessUnits)
  const originBusinessUnits =
    useAppSelector((state) => state.user.originBusinessUnits) ?? []

  const origin = formik.values.origin
  const errors = formik.errors.origin as
    | FormikErrors<OriginFormValues>
    | undefined
  const touched = formik.touched.origin as
    | FormikTouched<OriginFormValues>
    | undefined

  const [originSearch, setOriginSearch] = useState(
    origin.businessUnit
      ? `${origin.businessUnit.buCode} - ${origin.businessUnit.location?.name ?? ''}`
      : ''
  )

  // [D9] Los servicios dependen de la oficina origen: al cambiar la BU origen
  // (auto-set con 1 opción o selección con varias) refrescar lockedServices.
  const originBuCode = origin.businessUnit?.buCode
  useEffect(() => {
    if (!originBuCode) return
    let active = true
    getLockBUServices(originBuCode).then((res) => {
      if (active) dispatch(setLockedServices(res.model ?? []))
    })
    return () => {
      active = false
    }
  }, [originBuCode, dispatch])

  const applyOrigin = (bu?: BusinessUnitInterface) => {
    if (!bu) {
      formik.setFieldValue('origin', {
        businessUnit: undefined,
        city: '',
        cityCode: '',
        address: '',
      })
      setOriginSearch('')
      return
    }
    // Canonizar contra inmutable.businessUnits (catálogo que usa el
    // destinatario) para resolver ciudad/dirección/coords de forma consistente.
    const canonical = businessUnits.find((b) => b.buCode === bu.buCode) ?? bu
    const city = resolveCityFromBusinessUnit(canonical, originCities)
    formik.setFieldValue('origin', {
      businessUnit: canonical,
      city: city?.locationName ?? '',
      cityCode: city?.locationCode ?? '',
      address: canonical.location?.address ?? '',
    })
    setOriginSearch(`${canonical.buCode} - ${canonical.location?.name ?? ''}`)
  }

  // Con 1 sola oficina (o cuando el fetch perezoso la trae después), auto-set.
  useEffect(() => {
    if (originBusinessUnits.length === 1 && !origin.businessUnit) {
      applyOrigin(originBusinessUnits[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originBusinessUnits])

  // 0 oficinas → el bloqueo se maneja en CreateShipmentV2 (no renderiza combo).
  if (originBusinessUnits.length === 0) return null

  // 1 sola oficina → texto de solo lectura (estilo AccountDetails).
  if (originBusinessUnits.length === 1) {
    return (
      <div className="flex flex-1 flex-col text-xs gap-1">
        <div className="flex flex-1 gap-4">
          <div
            className="flex justify-end shrink-0"
            style={{ width: '8.5rem' }}
          >
            <span className="font-bold text-gray-600 text-right">
              Oficina origen:
            </span>
          </div>
          <span className="text-gray-800">
            {origin.businessUnit?.buCode} -{' '}
            {origin.businessUnit?.location?.name}
          </span>
        </div>
        <div className="flex flex-1 gap-4">
          <div
            className="flex justify-end shrink-0"
            style={{ width: '8.5rem' }}
          >
            <span className="font-bold text-gray-600 text-right">
              Ciudad origen:
            </span>
          </div>
          <span className="text-gray-800">
            {origin.city} {origin.cityCode ? `(${origin.cityCode})` : ''}
          </span>
        </div>
        <div className="flex flex-1 gap-4">
          <div
            className="flex justify-end shrink-0"
            style={{ width: '8.5rem' }}
          >
            <span className="font-bold text-gray-600 text-right">
              Dirección:
            </span>
          </div>
          <span className="text-gray-800">{origin.address}</span>
        </div>
      </div>
    )
  }

  // Varias oficinas → combo de búsqueda; arranca vacío y obliga elección [D4].
  return (
    <div className="flex flex-col gap-1">
      <FormSearch
        id="origin.businessUnit"
        name="origin.businessUnit"
        label="Oficina origen"
        placeholder="Ingresa la oficina"
        value={originSearch}
        options={originBusinessUnits}
        unselect={!!(originSearch.length > 0)}
        className="!h-8"
        labelClassname="!text-xs"
        labelContainerClassname="flex sm:!justify-end sm:w-32"
        containerClassname="flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row"
        error={
          touched?.businessUnit && errors?.businessUnit
            ? (errors?.businessUnit as string)
            : undefined
        }
        onBlur={(e) => {
          setTimeout(() => {
            if (
              !e.relatedTarget ||
              !e.relatedTarget.classList.contains('option-class')
            ) {
              formik.handleBlur(e)
            }
          }, 200)
        }}
        onChange={(e) => setOriginSearch(e.target.value)}
        onSelectOption={(option) => applyOrigin(option)}
        onFilter={filterBusinessUnit}
        style={{ maxWidth: '15rem' }}
        RenderOption={({ option }) => <BusinessUnitOption {...option} />}
      />

      <FormText
        id="origin.city"
        name="origin.city"
        label="Ciudad origen"
        value={origin.city}
        className="!h-8"
        labelClassname="!text-xs"
        labelContainerClassname="flex sm:!justify-end sm:w-32"
        containerClassname="flex flex-1 sm:ml-0 sm:gap-8 sm:items-center flex-col sm:flex-row"
        style={{ maxWidth: '15rem' }}
        disabled={true}
        readOnly={true}
      />

      <FormTextArea
        id="origin.address"
        name="origin.address"
        label="Dirección"
        value={origin.address}
        labelClassname="!text-xs"
        labelContainerClassname="flex sm:!justify-end sm:w-32 sm:mt-2"
        containerClassname="flex flex-1 sm:ml-0 sm:gap-8 flex-col sm:flex-row"
        disabled={true}
        readOnly={true}
      />
    </div>
  )
}
